import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";

const DISCOVERY_DOC = "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest";
const SCOPES = "https://www.googleapis.com/auth/drive.readonly";
const GAPI_SCRIPT_ID = "google-api-script";
const GIS_SCRIPT_ID = "google-identity-services-script";
const SHARED_DRIVE_ID =
	process.env.REACT_APP_GOOGLE_SHARED_DRIVE_ID || process.env.GOOGLE_SHARED_DRIVE_ID || "";
const SHARED_DRIVE_NAME =
	process.env.REACT_APP_GOOGLE_SHARED_DRIVE_NAME ||
	process.env.GOOGLE_SHARED_DRIVE_NAME ||
	"TSQS";
const CLIENT_ID =
	process.env.REACT_APP_GOOGLE_CLIENT_ID ||
	process.env.GOOGLE_CLIENT_ID ||
	"";
const GOOGLE_DRIVE_INTEGRATION_ENABLED =
	process.env.REACT_APP_ENABLE_GOOGLE_DRIVE_INTEGRATION === "true";

function formatGoogleApiError(error, fallbackMessage) {
	if (!error) {
		return fallbackMessage;
	}

	if (typeof error === "string") {
		return error;
	}

	if (typeof error.result?.error?.message === "string") {
		return error.result.error.message;
	}

	if (typeof error.error === "string") {
		return error.error;
	}

	if (typeof error.details === "string") {
		return error.details;
	}

	if (typeof error.body === "string") {
		try {
			const parsedBody = JSON.parse(error.body);
			if (typeof parsedBody?.error?.message === "string") {
				return parsedBody.error.message;
			}
		} catch {
			// Ignore JSON parsing failures and fall back to other fields.
		}
	}

	if (typeof error.message === "string") {
		return error.message;
	}

	return fallbackMessage;
}

function loadScript(id, src) {
	return new Promise((resolve, reject) => {
		const existingScript = document.getElementById(id);

		if (existingScript) {
			if (existingScript.dataset.loaded === "true") {
				resolve();
				return;
			}

			existingScript.addEventListener("load", resolve, { once: true });
			existingScript.addEventListener(
				"error",
				() => reject(new Error(`Failed to load script: ${src}`)),
				{ once: true },
			);
			return;
		}

		const script = document.createElement("script");
		script.id = id;
		script.src = src;
		script.async = true;
		script.defer = true;
		script.addEventListener(
			"load",
			() => {
				script.dataset.loaded = "true";
				resolve();
			},
			{ once: true },
		);
		script.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), {
			once: true,
		});
		document.body.appendChild(script);
	});
}

export default function GoogleDriveQuickstart() {
	const tokenClientRef = useRef(null);
	const [status, setStatus] = useState("Loading Google Drive client...");
	const [isReady, setIsReady] = useState(false);
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [isBusy, setIsBusy] = useState(false);
	const [error, setError] = useState("");
	const [files, setFiles] = useState([]);
	const [sharedDrive, setSharedDrive] = useState(null);

	useEffect(() => {
		if (!GOOGLE_DRIVE_INTEGRATION_ENABLED) {
			setStatus("Google Drive integration is disabled.");
			return undefined;
		}

		let isMounted = true;

		async function initializeClient() {
			if (!CLIENT_ID) {
				if (isMounted) {
					setError(
						"Missing Google client ID. In Create React App, expose it as REACT_APP_GOOGLE_CLIENT_ID.",
					);
					setStatus("Google Drive client is not configured.");
				}
				return;
			}

			try {
				await Promise.all([
					loadScript(GAPI_SCRIPT_ID, "https://apis.google.com/js/api.js"),
					loadScript(GIS_SCRIPT_ID, "https://accounts.google.com/gsi/client"),
				]);

				await new Promise((resolve, reject) => {
					window.gapi.load("client", {
						callback: resolve,
						onerror: () => reject(new Error("Failed to load Google API client.")),
					});
				});

				await window.gapi.client.init({
					discoveryDocs: [DISCOVERY_DOC],
				});

				tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
					client_id: CLIENT_ID,
					scope: SCOPES,
					callback: () => {},
				});

				if (!isMounted) {
					return;
				}

				setIsReady(true);
				setStatus("Google Drive client is ready.");
			} catch (initializationError) {
				if (!isMounted) {
					return;
				}

				setError(initializationError.message || "Failed to initialize Google Drive client.");
				setStatus("Google Drive client failed to initialize.");
			}
		}

		initializeClient();

		return () => {
			isMounted = false;
		};
	}, []);

	const getSharedDrive = async () => {
		if (SHARED_DRIVE_ID) {
			const configuredDrive = {
				id: SHARED_DRIVE_ID,
				name: SHARED_DRIVE_NAME || SHARED_DRIVE_ID,
			};
			setSharedDrive(configuredDrive);
			return configuredDrive;
		}

		const response = await window.gapi.client.drive.drives.list({
			pageSize: 100,
			fields: "drives(id, name)",
			q: `name = '${SHARED_DRIVE_NAME.replace(/'/g, "\\'")}'`,
			useDomainAdminAccess: false,
		});

		const matchingDrive = (response.result.drives || []).find(
			(drive) => drive.name === SHARED_DRIVE_NAME,
		);

		if (!matchingDrive) {
			throw new Error(`Shared drive "${SHARED_DRIVE_NAME}" was not found for this account.`);
		}

		setSharedDrive(matchingDrive);
		return matchingDrive;
	};

	const listFiles = async () => {
		setIsBusy(true);
		setError("");

		try {
			const targetDrive = sharedDrive || (await getSharedDrive());
			const response = await window.gapi.client.drive.files.list({
				pageSize: 10,
				fields: "files(id, name)",
				corpora: "drive",
				driveId: targetDrive.id,
				includeItemsFromAllDrives: true,
				supportsAllDrives: true,
			});
			const nextFiles = response.result.files || [];
			setFiles(nextFiles);
			setStatus(
				nextFiles.length > 0
					? `Loaded files from shared drive ${targetDrive.name}.`
					: `No files found in shared drive ${targetDrive.name}.`,
			);
		} catch (requestError) {
			console.error("Google Drive request failed:", requestError);
			setError(formatGoogleApiError(requestError, "Failed to list Google Drive files."));
			setStatus("Google Drive request failed.");
		} finally {
			setIsBusy(false);
		}
	};

	const handleAuthorize = () => {
		if (!GOOGLE_DRIVE_INTEGRATION_ENABLED) {
			setError("Google Drive integration is disabled.");
			return;
		}

		if (!tokenClientRef.current) {
			setError("Google token client is not ready yet.");
			return;
		}

		setIsBusy(true);
		setError("");

		tokenClientRef.current.callback = async (response) => {
			if (response.error) {
				setError(formatGoogleApiError(response, "Google authorization failed."));
				setStatus("Google authorization failed.");
				setIsBusy(false);
				return;
			}

			setIsAuthorized(true);
			await listFiles();
		};

		const existingToken = window.gapi.client.getToken();
		tokenClientRef.current.requestAccessToken({
			prompt: existingToken ? "" : "consent",
		});
	};

	const handleSignout = () => {
		const token = window.gapi.client.getToken();

		if (token?.access_token) {
			window.google.accounts.oauth2.revoke(token.access_token);
		}

		window.gapi.client.setToken("");
		setIsAuthorized(false);
		setFiles([]);
		setSharedDrive(null);
		setError("");
		setStatus("Signed out from Google Drive.");
	};

	return (
		<Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
			<CardContent>
				<Stack spacing={2}>
					<Box>
						<Typography variant="h6">Google Drive test connection</Typography>
						<Typography variant="body2" color="text.secondary">
							This is a React version of the Drive quickstart configured to read a shared drive.
							Use `REACT_APP_GOOGLE_SHARED_DRIVE_ID` to target a specific drive directly, or
							`REACT_APP_GOOGLE_SHARED_DRIVE_NAME` to resolve it by name.
						</Typography>
					</Box>

					{sharedDrive ? (
						<Alert severity="success">Connected to shared drive: {sharedDrive.name}</Alert>
					) : null}

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
						<Button variant="contained" onClick={handleAuthorize} disabled={!isReady || isBusy}>
							{isAuthorized ? "Refresh" : "Authorize"}
						</Button>
						<Button variant="outlined" onClick={handleSignout} disabled={!isAuthorized || isBusy}>
							Sign Out
						</Button>
						{isBusy ? <CircularProgress size={24} /> : null}
					</Stack>

					<Alert severity={error ? "error" : "info"}>{error || status}</Alert>

					{CLIENT_ID ? null : (
						<Alert severity="warning">
							Frontend builds only expose env vars prefixed with REACT_APP_. Set
							REACT_APP_GOOGLE_CLIENT_ID for this component.
						</Alert>
					)}

					<Alert severity="info">
						Shared drive lookup uses the `drive.readonly` scope because Google requires that
						scope for `drives.list`.
					</Alert>

					<Box
						sx={{
							p: 2,
							borderRadius: 1,
							bgcolor: "grey.100",
							fontFamily: "monospace",
							whiteSpace: "pre-wrap",
							minHeight: 120,
						}}
					>
						{files.length > 0
							? files.map((file) => `${file.name} (${file.id})`).join("\n")
							: "Files will appear here after authorization."}
					</Box>
				</Stack>
			</CardContent>
		</Card>
	);
}
